import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Link, usePage } from '@inertiajs/react'
import { ChevronDown, Building2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { accountSettingsPath } from '@/lib/account-settings'
import type { AccountPropertyGroup, CurrentAccount, AccountSettingsSection } from '@/types/shared'

// Each account is a SEPARATE BUSINESS ("portfolio"). They are never merged or shown
// together. This control ALWAYS displays exactly one business as the current context —
// there is no "all" view.
//
// `property`  — per-property header: switching businesses lands on that business's first property.
// `portfolio` — root briefing header: switching businesses opens that business's own briefing.
//
// EVERY DESTINATION COMES OFF THE TARGET GROUP'S OWN PATHS (`portfolioPath`, `settingsPath`,
// `properties[].basePath`) — never off the current page's, and never rebuilt from an id. This is
// the one payload in the app that spans businesses, which is exactly why it is the only place a
// cross-business URL can legitimately come from.
//
// The business also lives in the path now, so the old `?account_id=` rider is gone: it is tier 1
// in the backend's resolve_scoped_account and tier 0 (the path) already decides the answer.
type AccountSwitcherMode = 'property' | 'portfolio'

interface AccountSwitcherProps {
  properties: AccountPropertyGroup[]
  currentAccount: CurrentAccount | null
  mode?: AccountSwitcherMode
}

// Two-letter initials from the account name — mirrors the UserMenu avatar treatment.
function accountInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2)
}

// Account-context switcher, shared by both headers via the `mode` prop.
//
// The trigger always renders the current business name as context. It becomes an
// interactive dropdown ONLY when the user owns more than one business; with a single
// business it is a static, non-interactive label (no chevron, no menu). The dropdown
// lists the OTHER businesses to switch to — never an aggregated "all" option.
export default function AccountSwitcher({ properties, currentAccount, mode = 'property' }: AccountSwitcherProps) {
  // "Am I inside the settings panel?" is answered by the SERVER, via the presence of
  // `activeSection` — a settings page always carries one and no other page does. It used to be
  // guessed with `path.startsWith('/account/')`, which under /a/:account_slug/settings/… never
  // matches: every switch from a settings tab would fall through to the briefing branch and dump
  // the operator off the tab they were reading, silently and with no error.
  const activeSection = usePage<{ activeSection?: AccountSettingsSection }>().props.activeSection ?? null
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const groups = properties ?? []
  const isPortfolio = mode === 'portfolio'
  // More than one business is the only case worth switching between.
  const interactive = groups.length > 1

  const updatePos = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPos({ top: rect.bottom + 6, left: rect.left })
  }, [])

  useEffect(() => {
    if (!open) return
    updatePos()
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (buttonRef.current?.contains(target) || panelRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      document.removeEventListener('mousedown', handler)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, updatePos])

  // property mode: nothing renders until there is a property to switch among (single-
  // account users have no second business to pick, and the shared JumpToProperty already
  // covers moving between their properties). portfolio mode: always render so the business
  // name shows as context, even for a single-business user (just not interactive).
  if (!isPortfolio && groups.length <= 1) return null
  if (isPortfolio && groups.length === 0) return null

  // The trigger always shows the current BUSINESS name — never "all". Defensive fallback
  // covers the brief window before the concurrent backend props land.
  const activeName = currentAccount?.name ?? groups[0]?.accountName ?? 'Select business'

  // Where switching to `group` lands. On a settings page it is the SAME tab in the other business
  // (settingsPath + the tab's fixed suffix), so the operator keeps reading what they were reading;
  // everywhere else it is that business's briefing. Both paths are the target group's own.
  const switchHref = (group: AccountPropertyGroup): string =>
    activeSection ? accountSettingsPath(group.settingsPath, activeSection) : group.portfolioPath

  // Businesses other than the current one — the only switch targets that make sense.
  const otherGroups = groups.filter((group) => group.accountId !== currentAccount?.id)

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => interactive && setOpen(!open)}
        aria-label={interactive ? (isPortfolio ? 'Switch business' : 'Switch account') : undefined}
        aria-expanded={interactive ? open : undefined}
        disabled={!interactive}
        className={cn(
          'flex items-center gap-2 rounded-lg border border-border/60 bg-white py-1 pl-1 pr-2 text-xs font-medium text-foreground transition-colors',
          interactive ? 'hover:border-border cursor-pointer' : 'cursor-default',
        )}
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/10 text-[10px] font-semibold text-primary">
          {currentAccount || groups[0] ? accountInitials(activeName) : <Building2 className="h-3 w-3" />}
        </span>
        <span className="hidden max-w-[160px] truncate sm:inline-block">{activeName}</span>
        {interactive && (
          <ChevronDown
            className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200', open && 'rotate-180')}
          />
        )}
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className="fixed z-[9999] w-72 overflow-hidden rounded-xl border border-border/60 bg-white shadow-xl shadow-foreground/10 animate-slide-in"
            style={{ top: pos.top, left: pos.left }}
          >
            <div className="border-b border-border/40 px-3 py-2">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Switch business</div>
            </div>
            <div className="max-h-80 overflow-y-auto py-1">
              {otherGroups.map((group) => {
                const firstProperty = group.properties[0]
                const propertyCount = group.properties.length

                const inner = (
                  <>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-[11px] font-semibold text-primary">
                      {accountInitials(group.accountName)}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-foreground">{group.accountName}</span>
                      <span className="block text-xs text-muted-foreground">
                        {propertyCount === 0
                          ? 'No properties yet — set one up'
                          : propertyCount === 1
                            ? '1 property'
                            : `${propertyCount} properties`}
                      </span>
                    </span>
                  </>
                )

                // portfolio mode (and any settings page): this business's briefing, or the settings
                // tab the operator is already on. A real, distinct URL per business now — so it is
                // a <Link>, not a button that re-fetched the same path with a different ?account_id.
                if (isPortfolio || activeSection) {
                  return (
                    <Link
                      key={group.accountId}
                      href={switchHref(group)}
                      preserveScroll
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted"
                    >
                      {inner}
                    </Link>
                  )
                }

                // property mode: no property to land on yet → send the user to this business's own
                // portfolio, whose empty-state is the add-a-property onboarding entry. A property
                // page can't render for a business with no property, so we route to the portfolio
                // rather than leaving a dead/inert row.
                if (!firstProperty) {
                  return (
                    <Link
                      key={group.accountId}
                      href={group.portfolioPath}
                      onClick={() => setOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted"
                    >
                      {inner}
                    </Link>
                  )
                }

                // property mode: navigate to this business's first property, by ITS OWN path.
                return (
                  <Link
                    key={group.accountId}
                    href={firstProperty.basePath}
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-3 px-3 py-2.5 transition-colors hover:bg-muted"
                  >
                    {inner}
                  </Link>
                )
              })}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
